import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  TRENDS,
  TREND_LEADERBOARDS,
  ACTIVITY_PHASES,
  ACTIVITY_LABEL,
  formatPlays,
  timeRemaining,
} from "../mockData";
import { VideoEmbed, PlatformBadge, videoCanonicalUrl } from "../components/VideoEmbed";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  Crown,
  Music2,
  Upload as UploadIcon,
  Sparkles,
  Globe2,
  Share2,
} from "lucide-react";

export default function TrendDetail() {
  const { trendId } = useParams<{ trendId: string }>();
  const trend = TRENDS.find((t) => t.id === trendId);

  if (!trend) {
    return (
      <div className="card p-8 text-center text-slate-500">
        Trend not found.{" "}
        <Link to="/explore" className="text-el-700">Back to Explore</Link>
      </div>
    );
  }

  const board = TREND_LEADERBOARDS[trend.id] ?? [];
  const phases = ACTIVITY_PHASES[trend.activityId] ?? [];
  const closing = timeRemaining(trend.endsAt);

  // Country distribution from the leaderboard for a quick "global" feel
  const byCountry = board.reduce<Record<string, { flag: string; count: number }>>((acc, e) => {
    if (!acc[e.country]) acc[e.country] = { flag: e.flag, count: 0 };
    acc[e.country].count += 1;
    return acc;
  }, {});
  const countries = Object.entries(byCountry)
    .map(([code, v]) => ({ code, ...v }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/explore" className="inline-flex items-center gap-1 hover:text-slate-700">
          <ArrowLeft className="h-3.5 w-3.5" /> Explore
        </Link>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700">Trends</span>
        <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
        <span className="text-slate-700 font-medium">{trend.title}</span>
      </div>

      {/* Header — slim, no decorative banner */}
      <div className="card p-5 flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <span className="text-[0.65rem] uppercase tracking-[0.08em] font-semibold text-slate-700 bg-slate-100 rounded px-2 py-0.5">{trend.hashtag}</span>
            <span className="text-[0.65rem] uppercase tracking-[0.08em] font-semibold text-slate-700 bg-slate-100 rounded px-2 py-0.5">{trend.bpm} BPM</span>
            <span className="text-[0.65rem] uppercase tracking-[0.08em] font-semibold text-slate-700 bg-slate-100 rounded px-2 py-0.5">{ACTIVITY_LABEL[trend.activityId]}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/85 text-white text-[0.65rem] font-semibold px-2 py-0.5">
              <Clock className="h-3 w-3" />
              {closing}
            </span>
          </div>
          <h2 className="text-xl font-semibold tracking-tightish text-slate-900">
            {trend.title}
          </h2>
          <div className="text-sm text-slate-600 inline-flex items-center gap-1.5 mt-0.5">
            <Music2 className="h-3.5 w-3.5" />
            {trend.song} — {trend.artist}
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">started by {trend.originator}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Globe2 className="h-3.5 w-3.5" />
              {formatPlays(trend.plays)} global plays
            </span>
            <span>· {countries.length} countries on the board</span>
            <span>· started {trend.startedAt}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary !h-9">
            <Share2 className="h-4 w-4" /> Share
          </button>
          <button className="btn-primary !h-9">
            <UploadIcon className="h-4 w-4" /> Join trend
          </button>
        </div>
      </div>

      {/* Top performance — primary embed for the #1 entry */}
      {(() => {
        const top = board[0];
        const topVideo = top?.video ?? trend.referenceVideo;
        if (!topVideo) return null;
        return (
          <div className="card p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Crown className="h-4 w-4 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    Top performance{top ? ` · ${top.user}` : ""}
                  </div>
                  <div className="text-[0.7rem] text-slate-500 truncate">
                    {top
                      ? `#1 globally · ${(top.views / 1_000_000).toFixed(1)}M views · score ${top.score}`
                      : "Reference clip"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <PlatformBadge platform={topVideo.platform} />
                <a
                  href={videoCanonicalUrl(topVideo)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost !h-7 !text-[0.7rem]"
                >
                  Open
                </a>
              </div>
            </div>
            <div className="p-4">
              <VideoEmbed video={topVideo} title={`${trend.title} — top performance`} autoplay />
            </div>
          </div>
        );
      })()}

      {/* Leaderboard + side info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Leaderboard */}
        <div className="card lg:col-span-2 p-0 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Crown className="h-4 w-4 text-el-600" />
                <div className="text-sm font-semibold text-slate-900">Global leaderboard</div>
              </div>
              <div className="text-xs text-slate-500">
                {board.length} entries · top 100 lock when {trend.title} closes
              </div>
            </div>
            <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-el-500" />
              AI-scored
            </div>
          </div>
          <ol className="divide-y divide-slate-100">
            {board.map((e) => (
              <li key={e.rank} className="px-5 py-2.5 flex items-center gap-3">
                <span className={[
                  "w-6 text-center font-semibold tabular-nums shrink-0",
                  e.rank === 1 ? "text-amber-600 text-base" :
                  e.rank === 2 ? "text-slate-500" :
                  e.rank === 3 ? "text-amber-700/70" : "text-slate-400",
                ].join(" ")}>
                  {e.rank === 1 ? "🥇" : e.rank === 2 ? "🥈" : e.rank === 3 ? "🥉" : e.rank}
                </span>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-el-200 to-el-400 text-white text-[0.7rem] font-semibold flex items-center justify-center shrink-0">
                  {e.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
                    <span className="truncate">{e.user}</span>
                    {e.video && <PlatformBadge platform={e.video.platform} />}
                  </div>
                  <div className="text-[0.7rem] text-slate-500 inline-flex items-center gap-1.5">
                    <span className="text-base leading-none">{e.flag}</span>
                    {e.country} · {(e.views / 1_000_000).toFixed(1)}M views
                  </div>
                </div>
                {/* Score with bar */}
                <div className="hidden sm:flex items-center gap-2 w-32 shrink-0">
                  <div className="h-1.5 flex-1 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-fuchsia-400 to-amber-400"
                      style={{ width: `${e.score}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-900 tabular-nums w-7 text-right">
                    {e.score}
                  </span>
                </div>
                {e.video ? (
                  <a
                    href={videoCanonicalUrl(e.video)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost !h-7 !text-[0.7rem] shrink-0"
                  >
                    Watch
                  </a>
                ) : (
                  <button className="btn-ghost !h-7 !text-[0.7rem] shrink-0" disabled>Watch</button>
                )}
              </li>
            ))}
          </ol>
        </div>

        {/* Side rail */}
        <div className="space-y-4">
          {/* Countries */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Globe2 className="h-4 w-4 text-el-600" />
              <div className="text-sm font-semibold text-slate-900">Countries on the board</div>
            </div>
            <ul className="space-y-2">
              {countries.map((c) => (
                <li key={c.code} className="flex items-center gap-2 text-sm">
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="text-slate-700">{c.code}</span>
                  <span className="ml-auto text-xs text-slate-500 tabular-nums">{c.count}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Choreography phases */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Music2 className="h-4 w-4 text-el-600" />
              <div className="text-sm font-semibold text-slate-900">Choreography</div>
            </div>
            <ul className="space-y-2">
              {phases.map((p) => (
                <li key={p.id} className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[0.65rem] font-semibold text-fuchsia-600">{p.id}</span>
                    <span className="text-sm font-medium text-slate-900 truncate">{p.label}</span>
                    <span className="ml-auto text-[0.65rem] text-slate-400">at {p.pct}%</span>
                  </div>
                  {p.cue && (
                    <div className="text-[0.7rem] text-slate-500 mt-0.5">{p.cue}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Reference song video — demoted to secondary slot */}
          {trend.referenceVideo && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Music2 className="h-4 w-4 text-el-600 shrink-0" />
                  <div className="text-sm font-semibold text-slate-900 truncate">Reference song</div>
                </div>
                <PlatformBadge platform={trend.referenceVideo.platform} />
              </div>
              <VideoEmbed video={trend.referenceVideo} title={`${trend.title} — reference`} />
              <div className="mt-2 text-[0.7rem] text-slate-500 truncate">
                {trend.song} · {trend.artist}
              </div>
            </div>
          )}

          {/* AI scoring blurb */}
          <div className="card p-5 bg-gradient-to-br from-white to-fuchsia-50/40">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-fuchsia-50 text-fuchsia-600 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">How scoring works</div>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                  Eleetz reads your phone clip, locks each move to the beat at {trend.bpm} BPM, and
                  scores beat-sync, body-line accuracy and energy. No judges, no friend votes —
                  just the body-measure stream.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

